import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { config } from '../constants';
import './classInviteBanner.scss';

const url = config.url.API_URL;

export default function ClassInviteBanner({ isAuthenticated }) {
  const location = useLocation();
  const [invites, setInvites] = useState([]);

  const loadInvites = useCallback(async (signal) => {
    if (!isAuthenticated || location.pathname === '/logout') {
      setInvites([]);
      return;
    }

    try {
      const response = await fetch(`${url}/users/class-invites/pending`, {
        credentials: 'include',
        signal,
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && Array.isArray(data.invites)) {
        setInvites(data.invites);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      console.error('Error loading class invites:', error);
    }
  }, [isAuthenticated, location.pathname]);

  useEffect(() => {
    const controller = new AbortController();
    loadInvites(controller.signal);
    return () => controller.abort();
  }, [loadInvites]);

  useEffect(() => {
    const onInvitesUpdated = () => loadInvites();
    window.addEventListener('class-invites-updated', onInvitesUpdated);
    return () => window.removeEventListener('class-invites-updated', onInvitesUpdated);
  }, [loadInvites]);

  if (!isAuthenticated || invites.length === 0) {
    return null;
  }

  if (location.pathname.startsWith('/class-invite/')) {
    return null;
  }

  return (
    <div className="class-invite-banner container mt-3">
      <Alert variant="info" className="text-start mb-0">
        <Alert.Heading className="h5 mb-2">
          {invites.length === 1 ? 'You have a class invite' : `You have ${invites.length} class invites`}
        </Alert.Heading>
        {invites.map((invite) => (
          <div key={invite.id} className="d-flex flex-wrap align-items-center gap-2 mb-2">
            <span>
              <strong>{invite.teacherName || 'A teacher'}</strong>
              {invite.teacherEmail ? ` (${invite.teacherEmail})` : ''} invited you to{' '}
              <strong>{invite.className}</strong>. Class code: <code>{invite.classCode}</code>
            </span>
            <Button
              as={Link}
              to={`/class-invite/${invite.id}`}
              state={{ from: `${location.pathname}${location.search}` }}
              size="sm"
              className="class-invite-banner__review"
            >
              Review invite
            </Button>
          </div>
        ))}
      </Alert>
    </div>
  );
}
